import { useCurrency } from "@shared/selectors/globalSelectors";
import React from "react";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";

// Rate type enum values from backend
// 3 = FLAT, 4 = SLAB, 5 = TOU, 6 = SEASONAL, 7 = FIXED_VARIABLE
const RATE_TYPE = {
  FLAT: 3,
  SLAB: 4,
  TOU: 5,
  SEASONAL: 6,
  FIXED: 7,
} as const;

const getNormalizedRateType = (rateInfo: any): number => {
  const rawType =
    rateInfo?.rateType ??
    rateInfo?.rate_type ??
    rateInfo?.rateTypeDisplay ??
    rateInfo?.rate_type_display;
  if (rawType === undefined || rawType === null) return 0;

  const str = String(rawType).trim().toUpperCase();

  if (str === "3" || str === "FLAT" || str.includes("FLAT"))
    return RATE_TYPE.FLAT;
  if (str === "4" || str === "SLAB" || str.includes("SLAB"))
    return RATE_TYPE.SLAB;
  if (
    str === "5" ||
    str === "TOU" ||
    str.includes("TOU") ||
    str.includes("TIME OF USE")
  )
    return RATE_TYPE.TOU;
  if (str === "6" || str === "SEASONAL" || str.includes("SEASONAL"))
    return RATE_TYPE.SEASONAL;
  if (
    str === "7" ||
    str === "FIXED" ||
    str === "FIXED_VARIABLE" ||
    str.includes("FIXED")
  )
    return RATE_TYPE.FIXED;

  const num = Number(str);
  if (!isNaN(num)) return num;

  return 0;
};

// Deduplicate rate items based on range and rate
const getUniqueRateItems = (items: any[]) => {
  if (!Array.isArray(items)) return [];
  const map = new Map<string, any>();
  items.forEach((item) => {
    const key = `${item.from ?? item.startTime ?? item.start_time ?? 0}-${
      item.to ?? item.endTime ?? item.end_time ?? "∞"
    }-${item.rate}`;
    if (!map.has(key)) {
      map.set(key, item);
    }
  });
  return Array.from(map.values());
};

interface Props {
  planInfo: any;
  ratesInfo: any[];
}

const ChargeBreakdown: React.FC<Props> = ({ planInfo, ratesInfo }) => {
  const currency = useCurrency();
  const commonCharges = planInfo?.extraCharges?.commonCharges ?? {};

  const renderSubTotal = (subTotal: number | string | null | undefined) => {
    if (subTotal === null || subTotal === undefined || subTotal === "")
      return null;
    const strVal = String(subTotal);
    return (
      <div className="flex justify-end items-center gap-6 py-3 px-4 border-t text-sm">
        <span className="font-bold text-gray-700">Sub-total</span>
        <span className="font-bold text-gray-900 text-base">
          {typeof subTotal === "number"
            ? `${currency} ${subTotal.toFixed(2)}`
            : strVal.startsWith(currency)
              ? strVal
              : `${currency} ${strVal}`}
        </span>
      </div>
    );
  };

  const renderRates = (rateInfo: any) => {
    const rateType = getNormalizedRateType(rateInfo);
    const rawRateItems: any[] = rateInfo?.rates ?? [];
    const rateItems = getUniqueRateItems(rawRateItems);

    const hasUnitsCount = rateItems.some(
      (item) =>
        item.units !== undefined &&
        item.units !== null &&
        item.units !== "-" &&
        item.units !== "",
    );

    switch (rateType) {
      // ─── 3: FLAT ─────────────────────────────────────────────
      case RATE_TYPE.FLAT: {
        const rate = rateItems[0]?.rate ?? 0;
        const unit = rateItems[0]?.unit ?? rateItems[0]?.units ?? "";
        const amount =
          rateItems[0]?.amount ?? rate * (rateInfo?.avgConsumption || 1);

        const columns: TableColumn<any>[] = [
          {
            key: "tier",
            header: "Tier / description",
            render: (val: any) => (
              <span className="font-semibold text-gray-800">{val}</span>
            ),
          },
          ...(hasUnitsCount
            ? [
                {
                  key: "units",
                  header: "Units",
                  render: (val: any) => (
                    <span className="text-gray-600">{val}</span>
                  ),
                },
              ]
            : []),
          {
            key: "rate",
            header: "Rate",
            render: (val: any) => <span className="text-gray-700">{val}</span>,
          },
          {
            key: "amount",
            header: "Amount",
            render: (val: any) => (
              <span className="font-bold text-gray-900">{val}</span>
            ),
          },
        ];

        const data = [
          {
            id: "flat-0",
            tier:
              rateInfo?.rateName ??
              rateInfo?.rate_name ??
              `${rateInfo?.serviceType ?? rateInfo?.service_type ?? ""} Flat Rate`.trim(),
            units: rateItems[0]?.units ?? "-",
            rate: `${currency} ${rate}${unit ? ` / ${unit}` : ""}`,
            amount: `${currency} ${
              typeof amount === "number" ? amount.toFixed(2) : amount
            }`,
          },
        ];

        return (
          <div className="border rounded-lg overflow-hidden bg-white">
            <GenericTable data={data} columns={columns} rowKey="id" />
            {renderSubTotal(
              typeof amount === "number" ? amount.toFixed(2) : amount,
            )}
          </div>
        );
      }

      // ─── 4: SLAB ─────────────────────────────────────────────
      case RATE_TYPE.SLAB: {
        let calculatedSubTotal = 0;
        const data = rateItems.map((item: any, index: number) => {
          const itemAmount =
            item.amount ??
            (item.rate ? Number(item.rate) * (Number(item.units) || 1) : 0);
          calculatedSubTotal += Number(itemAmount) || 0;
          return {
            id: `slab-${index}`,
            tier:
              item.label ??
              item.tierName ??
              item.tier_name ??
              `Tier ${index + 1}`,
            range: `${item.from ?? item.startTime ?? 0}–${
              item.to ?? item.endTime ?? "∞"
            } ${item.unit || ""}`.trim(),
            units: item.units ?? "-",
            rate: `${currency} ${item.rate}${item.isFlat ? " flat" : ""}`,
            amount: `${currency} ${
              typeof itemAmount === "number"
                ? itemAmount.toFixed(2)
                : itemAmount
            }`,
          };
        });

        const columns: TableColumn<any>[] = [
          {
            key: "tier",
            header: "Tier / description",
            render: (val: any) => (
              <span className="font-semibold text-gray-800">{val}</span>
            ),
          },
          {
            key: "range",
            header: "Range",
            render: (val: any) => <span className="text-gray-600">{val}</span>,
          },
          ...(hasUnitsCount
            ? [
                {
                  key: "units",
                  header: "Units",
                  render: (val: any) => (
                    <span className="text-gray-600">{val}</span>
                  ),
                },
              ]
            : []),
          {
            key: "rate",
            header: "Rate",
            render: (val: any) => <span className="text-gray-700">{val}</span>,
          },
          {
            key: "amount",
            header: "Amount",
            render: (val: any) => (
              <span className="font-bold text-gray-900">{val}</span>
            ),
          },
        ];

        return (
          <div className="border rounded-lg overflow-hidden bg-white">
            <GenericTable data={data || []} columns={columns} rowKey="id" />
            {renderSubTotal(calculatedSubTotal.toFixed(2))}
          </div>
        );
      }

      // ─── 5: TOU (Time of Use) ────────────────────────────────
      case RATE_TYPE.TOU: {
        let calculatedSubTotal = 0;
        const data = rateItems.map((item: any, index: number) => {
          const itemAmount = item.amount ?? 0;
          calculatedSubTotal += Number(itemAmount) || 0;
          return {
            id: `tou-${index}`,
            tier: item.period ?? item.label ?? `Period ${index + 1}`,
            range: `${item.startTime ?? item.start_time ?? "—"} – ${
              item.endTime ?? item.end_time ?? "—"
            }`,
            units: item.units ?? "-",
            rate: `${currency} ${item.rate}`,
            amount: `${currency} ${
              typeof itemAmount === "number"
                ? itemAmount.toFixed(2)
                : itemAmount
            }`,
          };
        });

        const columns: TableColumn<any>[] = [
          {
            key: "tier",
            header: "Tier / description",
            render: (val: any) => (
              <span className="font-semibold text-gray-800 capitalize">
                {val}
              </span>
            ),
          },
          {
            key: "range",
            header: "Time Window",
            render: (val: any) => <span className="text-gray-600">{val}</span>,
          },
          ...(hasUnitsCount
            ? [
                {
                  key: "units",
                  header: "Units",
                  render: (val: any) => (
                    <span className="text-gray-600">{val}</span>
                  ),
                },
              ]
            : []),
          {
            key: "rate",
            header: "Rate",
            render: (val: any) => <span className="text-gray-700">{val}</span>,
          },
          {
            key: "amount",
            header: "Amount",
            render: (val: any) => (
              <span className="font-bold text-gray-900">{val}</span>
            ),
          },
        ];

        return (
          <div className="border rounded-lg overflow-hidden bg-white">
            <GenericTable data={data} columns={columns} rowKey="id" />
            {calculatedSubTotal > 0 &&
              renderSubTotal(calculatedSubTotal.toFixed(2))}
          </div>
        );
      }

      // ─── 6: SEASONAL ─────────────────────────────────────────
      case RATE_TYPE.SEASONAL: {
        let calculatedSubTotal = 0;
        const data = rateItems.map((item: any, index: number) => {
          const itemAmount = item.amount ?? 0;
          calculatedSubTotal += Number(itemAmount) || 0;
          return {
            id: `seasonal-${index}`,
            tier:
              item.season ?? item.month ?? item.label ?? `Season ${index + 1}`,
            range: `${item.startMonth ?? item.start_month ?? item.startTime ?? "—"} – ${
              item.endMonth ?? item.end_month ?? item.endTime ?? "—"
            }`,
            units: item.units ?? "-",
            rate: `${currency} ${item.rate}`,
            amount: `${currency} ${
              typeof itemAmount === "number"
                ? itemAmount.toFixed(2)
                : itemAmount
            }`,
          };
        });

        const columns: TableColumn<any>[] = [
          {
            key: "tier",
            header: "Tier / description",
            render: (val: any) => (
              <span className="font-semibold text-gray-800 capitalize">
                {val}
              </span>
            ),
          },
          {
            key: "range",
            header: "Period",
            render: (val: any) => <span className="text-gray-600">{val}</span>,
          },
          ...(hasUnitsCount
            ? [
                {
                  key: "units",
                  header: "Units",
                  render: (val: any) => (
                    <span className="text-gray-600">{val}</span>
                  ),
                },
              ]
            : []),
          {
            key: "rate",
            header: "Rate",
            render: (val: any) => <span className="text-gray-700">{val}</span>,
          },
          {
            key: "amount",
            header: "Amount",
            render: (val: any) => (
              <span className="font-bold text-gray-900">{val}</span>
            ),
          },
        ];

        return (
          <div className="border rounded-lg overflow-hidden bg-white">
            <GenericTable data={data || []} columns={columns} rowKey="id" />
            {calculatedSubTotal > 0 &&
              renderSubTotal(calculatedSubTotal.toFixed(2))}
          </div>
        );
      }

      // ─── 7: FIXED_VARIABLE (fixed per billing period) ────────
      case RATE_TYPE.FIXED: {
        const fixedRate = rateItems[0]?.rate ?? 0;
        const amount = rateItems[0]?.amount ?? fixedRate;

        const columns: TableColumn<any>[] = [
          {
            key: "tier",
            header: "Tier / description",
            render: (val: any) => (
              <span className="font-semibold text-gray-800">{val}</span>
            ),
          },
          {
            key: "rate",
            header: "Rate",
            render: (val: any) => <span className="text-gray-700">{val}</span>,
          },
          {
            key: "amount",
            header: "Amount",
            render: (val: any) => (
              <span className="font-bold text-gray-900">{val}</span>
            ),
          },
        ];

        const data = [
          {
            id: "fixed-0",
            tier:
              rateInfo?.rateName ??
              rateInfo?.rate_name ??
              `${rateInfo?.serviceType ?? rateInfo?.service_type ?? ""} Fixed Rate`.trim(),
            rate: `${currency} ${fixedRate}`,
            amount: `${currency} ${
              typeof amount === "number" ? amount.toFixed(2) : amount
            }`,
          },
        ];

        return (
          <div className="border rounded-lg overflow-hidden bg-white">
            <GenericTable data={data} columns={columns} rowKey="id" />
            {renderSubTotal(
              typeof amount === "number" ? amount.toFixed(2) : amount,
            )}
          </div>
        );
      }

      default:
        return (
          <div className="text-gray-500 p-4 border rounded-lg text-sm bg-white">
            Rate type {rateInfo?.rateType ?? rateInfo?.rate_type ?? "Unknown"} (
            {rateInfo?.rateTypeDisplay ??
              rateInfo?.rate_type_display ??
              "Unknown"}
            ) — display not configured.
          </div>
        );
    }
  };

  const commonChargesList = Object.values(commonCharges);
  const commonChargesData = commonChargesList.map(
    (charge: any, idx: number) => ({
      id: `common-${idx}`,
      description: charge.chargeLabel || charge.name || "Charge",
      amount: `${currency} ${charge.fee || charge.amount || 0}`,
    }),
  );

  const commonChargesColumns: TableColumn<any>[] = [
    {
      key: "description",
      header: "Description",
      render: (val: any) => (
        <span className="font-semibold text-gray-800">{val}</span>
      ),
    },
    {
      key: "amount",
      header: "Amount",
      render: (val: any) => (
        <span className="font-bold text-gray-900">{val}</span>
      ),
    },
  ];

  const commonChargesSubTotal = commonChargesList.reduce(
    (sum: number, c: any) => sum + (Number(c.fee || c.amount) || 0),
    0,
  );

  return (
    <div className="space-y-8">
      {/* PLAN HEADER */}
      {planInfo?.planName && (
        <div className="pb-2">
          <h2 className="text-lg font-bold text-gray-900">
            {planInfo?.planName}
          </h2>
          <p className="text-sm text-gray-500">
            {planInfo?.category} · {planInfo?.utilityService?.join(", ")}
          </p>
        </div>
      )}

      {/* COMMON CHARGES */}
      {commonChargesList.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-base font-semibold text-gray-900">
            Common Charges
          </h3>
          <div className="border rounded-lg overflow-hidden bg-white">
            <GenericTable
              data={commonChargesData}
              columns={commonChargesColumns}
              rowKey="id"
            />
            {renderSubTotal(commonChargesSubTotal.toFixed(2))}
          </div>
        </div>
      )}

      {/* RATES */}
      {ratesInfo?.map((rateInfo, index) => {
        const rateName = rateInfo.rateName ?? rateInfo.rate_name;
        const rateTypeDisplay =
          rateInfo.rateTypeDisplay ??
          rateInfo.rate_type_display ??
          rateInfo.rateType ??
          rateInfo.rate_type;

        return (
          <div key={index} className="space-y-3">
            <div>
              <h3 className="text-base font-semibold text-gray-900">
                {rateInfo.serviceType ?? rateInfo.service_type} Rates
              </h3>
              {(rateName || rateTypeDisplay) && (
                <p className="text-xs text-gray-500">
                  {rateName ? `${rateName} · ` : ""}
                  {rateTypeDisplay}
                </p>
              )}
            </div>
            {renderRates(rateInfo)}
          </div>
        );
      })}
    </div>
  );
};

export default ChargeBreakdown;
